import React, { useState, useRef, useEffect } from 'react';
import { 
  Box, Button, Typography, CircularProgress, Paper, IconButton, 
  InputBase, List, ListItem, ListItemText, ListItemIcon, Divider 
} from '@mui/material';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import API from '../api/api';

export default function MapPicker({ initialCoords, onConfirm, onCancel }) {
  const mapContainer = useRef(null);
  const mapInstance = useRef(null);
  
  const [addressStr, setAddressStr] = useState("Fetching location...");
  const [isFetching, setIsFetching] = useState(true);
  const [currentCenter, setCurrentCenter] = useState({
    lat: initialCoords?.lat || 22.5726,
    lng: initialCoords?.lng || 88.3639
  });

  const [pinMoved, setPinMoved] = useState(false);

  // --- AUTOCOMPLETE STATES ---
  const [olaApiKey, setOlaApiKey] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const isSelectingRef = useRef(false);

  useEffect(() => {
    if (mapInstance.current) return;
    let debounceTimer; 

    const fetchMapConfig = async () => {
      try {
        // 1. Check Session Storage first
        const cachedKey = sessionStorage.getItem("ola_api_key");
        const cachedStyle = sessionStorage.getItem("ola_map_style");

        if (cachedKey && cachedStyle) {
            setOlaApiKey(cachedKey);
            initializeMap(cachedKey, JSON.parse(cachedStyle));
            return;
        }

        // 2. If not cached, fetch from APIs
        const res = await API.get("/config/ola-maps");
        const apiKey = res.data.api_key;
        setOlaApiKey(apiKey);
        
        const styleRes = await fetch(`https://api.olamaps.io/tiles/vector/v1/styles/default-light-standard/style.json?api_key=${apiKey}`);
        const styleJson = await styleRes.json();
        
        if (styleJson.layers) {
          styleJson.layers = styleJson.layers.filter(layer => layer.id !== '3d_model_data');
        }

        // 3. Save to Session Storage for next time
        sessionStorage.setItem("ola_api_key", apiKey);
        sessionStorage.setItem("ola_map_style", JSON.stringify(styleJson));

        initializeMap(apiKey, styleJson);
      } catch (error) { 
        console.error("Config fetch failed", error);
        setAddressStr("Map configuration error.");
      }
    };

    const initializeMap = (apiKey, cleanStyleJson) => {
      mapInstance.current = new maplibregl.Map({
        container: mapContainer.current,
        style: cleanStyleJson, // 👇 FIX: 3. Pass the clean JSON object instead of the URL
        center: [currentCenter.lng, currentCenter.lat],
        zoom: 15, 
        attributionControl: false,
        transformRequest: (url, resourceType) => {
          if (url.includes("olamaps.io")) {
            return {
              url: url + (url.includes("?") ? "&" : "?") + `api_key=${apiKey}`
            };
          }
          return { url };
        }
      });

      mapInstance.current.on('dragstart', () => {
        setPinMoved(true);
        setShowSuggestions(false);
        setIsSearching(false);
        if (document.activeElement instanceof HTMLElement) {
          document.activeElement.blur();
        }
      });
      
      mapInstance.current.on('zoomstart', () => setPinMoved(true));

      mapInstance.current.on('moveend', () => {
        const center = mapInstance.current.getCenter();
        setCurrentCenter({ lat: center.lat, lng: center.lng });
        
        setIsFetching(true);
        setAddressStr("Fetching location..."); 
        
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(async () => {
          try {
            const res = await API.post("/location/reverse-geocode", { lat: center.lat, lng: center.lng });
            setAddressStr(res.data.formatted_address);
          } catch (error) {
            setAddressStr("Could not fetch address details.");
          } finally {
            setIsFetching(false);
          }
        }, 800); 
      });

      mapInstance.current.once('load', () => {
        // 1. Remove Ola's broken 3D layer to fix the "3d_model" console error
        if (mapInstance.current.getLayer('3d_model_data')) {
          mapInstance.current.removeLayer('3d_model_data');
        }
        
        // 2. Catch missing images (like 'aquarium') to stop the red error spam
        mapInstance.current.on('styleimagemissing', (e) => {
           console.warn(`Ola Maps is missing icon: ${e.id}`);
        });

        // 3. Trigger the initial location fetch
        mapInstance.current.fire('moveend');
      });
    };

    fetchMapConfig();

    return () => {
      if (mapInstance.current) {
        mapInstance.current.remove();
        mapInstance.current = null;
      }
      clearTimeout(debounceTimer); 
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- DEBOUNCED SEARCH API CALL ---
  useEffect(() => {
    if (isSelectingRef.current) {
      isSelectingRef.current = false;
      return;
    }

    const timer = setTimeout(async () => {
      if (searchInput.trim().length > 2 && olaApiKey) {
        try {
          const url = `https://api.olamaps.io/places/v1/autocomplete?input=${encodeURIComponent(searchInput)}&api_key=${olaApiKey}`;
          const res = await fetch(url);
          const data = await res.json();
          if (data.predictions) {
            setSuggestions(data.predictions);
            setShowSuggestions(true);
          }
        } catch (error) {
          console.error("Autocomplete error:", error);
        }
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    }, 600);

    return () => clearTimeout(timer);
  }, [searchInput, olaApiKey]);

  // --- HANDLE SUGGESTION CLICK ---
  const handleSelectSuggestion = (prediction) => {
    isSelectingRef.current = true; 
    setSearchInput(prediction.structured_formatting?.main_text || prediction.description);
    
    // Hide dropdown and restore the back button
    setShowSuggestions(false);
    setSuggestions([]); 
    setIsSearching(false); 

    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    
    if (prediction.geometry && prediction.geometry.location) {
      const { lat, lng } = prediction.geometry.location;
      if (mapInstance.current) {
        setPinMoved(true); 
        mapInstance.current.flyTo({ center: [lng, lat], zoom: 16 });
      }
    }
  };

  const addressParts = addressStr.split(',');
  const addressTitle = addressParts[0] || "Fetching location...";
  const addressSubtitle = addressParts.slice(1).join(',').trim() || "";

  return (
    <Box sx={{ width: '100%', height: '100%', flexGrow: 1, position: 'relative', bgcolor: '#FFFDF8', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      
      {/* MAP LAYER */}
      <Box ref={mapContainer} sx={{ width: '100%', height: '100%', flexGrow: 1 }} />

      {/* 👇 NEW: BLUR BACKDROP OVERLAY */}
      <Box 
        onClick={() => {
          // If the user clicks the blurred background, abort the search
          setIsSearching(false);
          setShowSuggestions(false);
          if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
        }}
        sx={{
          position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
          bgcolor: 'rgba(255, 255, 255, 0.4)', // Light white tint
          backdropFilter: 'blur(8px)', // Triggers the modern blur
          WebkitBackdropFilter: 'blur(8px)', // For Safari support
          zIndex: 35, // Sits above map and bottom container, but below search bar
          opacity: isSearching ? 1 : 0,
          pointerEvents: isSearching ? 'auto' : 'none',
          transition: 'opacity 0.3s ease'
        }}
      />

      {/* FLOATING TOP BAR (Back Button + Search Bar) */}
      <Box sx={{ 
        position: 'absolute', 
        top: { xs: 'calc(env(safe-area-inset-top, 0px) + 20px)', sm: 24 }, // 👈 FIX: Cleaner safe area offset
        left: 16, 
        right: 16, 
        zIndex: 40, 
        display: 'flex', 
        gap: 1.5 
      }}>
        
        {/* Conditionally render the Back Button based on isSearching */}
        {!isSearching && (
          <IconButton 
            onClick={onCancel}
            sx={{ 
              bgcolor: '#FFFFFF', color: '#3A2C23', width: 48, height: 48, flexShrink: 0,
              boxShadow: '0 6px 20px rgba(58, 44, 35, 0.1)', border: '1px solid transparent',
              transition: 'all 0.3s ease',
              '&:hover': { bgcolor: '#F2EEE8' }
            }}
          >
            <ArrowBackIcon />
          </IconButton>
        )}

        {/* FLOATING SEARCH BAR & DROPDOWN */}
        <Box sx={{ flex: 1, position: 'relative', transition: 'all 0.3s ease' }}>
          
          <Paper elevation={0} sx={{ 
              p: '2px 16px', display: 'flex', alignItems: 'center', 
              borderRadius: showSuggestions && suggestions.length > 0 ? '24px 24px 0 0' : '28px', 
              border: '1px solid',
              borderColor: showSuggestions && suggestions.length > 0 ? '#ECE5DA' : 'transparent',
              boxShadow: '0 6px 20px rgba(58, 44, 35, 0.1)', bgcolor: '#FFFFFF', height: 48,
              transition: 'border-radius 0.2s ease',
              zIndex: 41
          }}>
            <SearchIcon sx={{ color: '#A59D96', mr: 1 }} />
            <InputBase
              placeholder="Search area or building..."
              value={searchInput}
              onChange={(e) => {
                setSearchInput(e.target.value);
                setIsSearching(true);
              }}
              onFocus={() => {
                setIsSearching(true); // 👇 Immediately triggers the blur overlay
                if (suggestions.length > 0) setShowSuggestions(true);
              }}
              sx={{ flex: 1, color: '#3A2C23', fontSize: '0.95rem', fontWeight: 600, '&::placeholder': { color: '#A59D96', opacity: 1 } }}
            />
            {searchInput && (
              <IconButton size="small" onClick={() => { 
                isSelectingRef.current = true;
                setSearchInput(''); 
                setSuggestions([]); 
                setShowSuggestions(false);
                setIsSearching(false); // Restore back button on Clear
              }} sx={{ color: '#A59D96' }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
          </Paper>

          {/* Autocomplete Dropdown List */}
          {showSuggestions && suggestions.length > 0 && (
            <Paper elevation={0} sx={{ 
              position: 'absolute', top: 47, left: 0, right: 0, 
              borderRadius: '0 0 24px 24px', overflow: 'hidden', bgcolor: '#FFFFFF', 
              border: '1px solid #ECE5DA', borderTop: '1px solid #F5F0E6', zIndex: 40, 
              maxHeight: 'calc(100vh - 100px)', // 👇 FIX: Allows dropdown to extend to the bottom of screen
              overflowY: 'auto',
              boxShadow: '0 16px 32px rgba(58, 44, 35, 0.12)' 
            }}>
              <List disablePadding>
                {suggestions.map((pred, idx) => (
                  <React.Fragment key={pred.place_id || idx}>
                    <ListItem 
                      onClick={() => handleSelectSuggestion(pred)}
                      sx={{ cursor: 'pointer', py: 1.5, px: 2, '&:hover': { bgcolor: '#FFFDF8' } }}
                    >
                      <ListItemIcon sx={{ minWidth: 42 }}>
                        <Box sx={{ bgcolor: '#FFF6DE', p: 0.8, borderRadius: '10px', display: 'flex' }}>
                          <LocationOnIcon sx={{ color: '#F29D17', fontSize: 20 }} />
                        </Box>
                      </ListItemIcon>
                      <ListItemText 
                        primary={<Typography variant="body2" fontWeight="800" color="#3A2C23" noWrap>{pred.structured_formatting?.main_text || pred.description}</Typography>}
                        secondary={pred.structured_formatting?.secondary_text && <Typography variant="caption" fontWeight="600" color="#8E867E" noWrap>{pred.structured_formatting.secondary_text}</Typography>}
                      />
                    </ListItem>
                    {idx < suggestions.length - 1 && <Divider sx={{ mx: 2, borderColor: '#F2EEE8' }} />}
                  </React.Fragment>
                ))}
              </List>
            </Paper>
          )}

        </Box>
      </Box>

      {/* FIXED CENTER PIN WITH TOOLTIP */}
      <Box sx={{ 
        position: 'absolute', top: '45%', left: '50%', transform: 'translate(-50%, -100%)', 
        zIndex: 10, pointerEvents: 'none', display: 'flex', flexDirection: 'column', alignItems: 'center' 
      }}>
        {!pinMoved && (
          <Box sx={{
            bgcolor: '#1A1A1A', color: '#FFFFFF', px: 2, py: 1, borderRadius: '8px',
            fontSize: '0.75rem', fontWeight: 600, mb: 1,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)', position: 'relative',
            '&::after': {
              content: '""', position: 'absolute', bottom: -5, left: '50%',
              transform: 'translateX(-50%)', borderWidth: '5px 5px 0',
              borderStyle: 'solid', borderColor: '#1A1A1A transparent transparent transparent'
            }
          }}>
            Move pin to your exact delivery location
          </Box>
        )}
        <LocationOnIcon sx={{ fontSize: 52, color: '#E85D3F', filter: 'drop-shadow(0px 8px 6px rgba(0,0,0,0.3))', mt: -1 }} />
      </Box>

      {/* RESPONSIVE BOTTOM CONTAINER */}
      <Box sx={{
        position: 'absolute', bottom: 16, left: 16, right: 16,
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
        zIndex: 30, // 👇 FIX: Reduced from 100 to 30 so the new Blur Backdrop covers it!
        pointerEvents: 'none' 
      }}>

        {/* CURRENT LOCATION BUTTON */}
        <Button 
          variant="contained" 
          startIcon={<MyLocationIcon />}
          onClick={() => {
            setPinMoved(true);
            navigator.geolocation.getCurrentPosition((pos) => {
              if (mapInstance.current) {
                mapInstance.current.flyTo({ center: [pos.coords.longitude, pos.coords.latitude], zoom: 16 });
              }
            });
          }}
          sx={{ 
            pointerEvents: 'auto', 
            bgcolor: '#FFFFFF', color: '#E85D3F', borderRadius: '24px', 
            px: 2.5, py: 1, boxShadow: '0 4px 16px rgba(58, 44, 35, 0.12)', 
            '&:hover': { bgcolor: '#FFFDF8' }, textTransform: 'none', fontWeight: 800,
            whiteSpace: 'nowrap', fontSize: '0.85rem'
          }}
        >
          Use current location
        </Button>

        {/* FLOATING BOTTOM CONFIRMATION CARD */}
        <Paper sx={{ 
          pointerEvents: 'auto', width: '100%', borderRadius: '20px', p: 2, pb: 2, 
          boxShadow: '0 8px 32px rgba(58, 44, 35, 0.12)', border: '1px solid #ECE5DA',
          bgcolor: '#FFFDF8', boxSizing: 'border-box'
        }}>

          <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
            <LocationOnIcon sx={{ color: '#E85D3F', mr: 1.5, mt: 0.2, fontSize: 26, flexShrink: 0 }} />
            <Box sx={{ minWidth: 0, flex: 1 }}>
              {isFetching ? (
                <CircularProgress size={20} sx={{ color: '#E85D3F', mt: 0.5 }} />
              ) : (
                <>
                  <Typography variant="h6" fontWeight="900" color="#3A2C23" noWrap sx={{ lineHeight: 1.2, mb: 0.5, fontSize: '1.05rem' }}>
                    {addressTitle}
                  </Typography>
                  <Typography variant="body2" color="#6E655E" fontWeight="600" sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                    {addressSubtitle}
                  </Typography>
                </>
              )}
            </Box>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button 
              variant="contained" disableElevation
              disabled={isFetching || addressStr.includes("Could not") || addressStr.includes("error")}
              onClick={() => onConfirm({ 
                lat: currentCenter.lat, 
                lng: currentCenter.lng, 
                formatted_address: addressStr 
              })}
              sx={{ 
                width: '85%', py: 1.2, borderRadius: '12px', 
                bgcolor: '#F8C332', color: '#3A2C23', fontWeight: '900', fontSize: '0.95rem', 
                textTransform: 'none', '&:hover': { bgcolor: '#F5B71D' } 
              }}
            >
              Confirm & proceed
            </Button>
          </Box>
        </Paper>

      </Box>
    </Box>
  );
}